import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { LinearProgress } from '@mui/material';
import { ChallengeDetailsFragment } from 'generated/graphql';
import StepperComponent, { Stepper } from 'retired/Stepper';
import Step from 'retired/Stepper/Step';
import StepLabel from 'retired/Stepper/StepLabel';
import PageHeader from 'retired/PageHeader';
import { ChallengeFormData, TeamOption, getDefaultChallengeFormData } from './types';
import { removeTypename } from './utils';
import {
  BasicInfoStep,
  DescriptionStep,
  BackgroundImageStep,
  CompetitionTypeStep,
  TeamsStep,
  SettingsStep,
  ReviewStep,
} from './components';

interface ChallengeFormProps {
  challenge?: ChallengeDetailsFragment;
  teams: TeamOption[];
  isLoading?: boolean;
  onSubmit: (data: ChallengeFormData) => void;
  onCancel: () => void;
}

export const getChallengeFormHeaderTitle = (challenge?: { title?: string }): string => {
  if (challenge) {
    const { title } = challenge;
    return `Edit "${title}" challenge`;
  }
  return 'Create challenge';
};

const ChallengeForm: React.FC<ChallengeFormProps> = ({
  challenge,
  teams,
  isLoading = false,
  onSubmit,
  onCancel,
}) => {
  const isEditing = !!challenge?.id;

  const defaultValues = challenge
    ? {
        ...getDefaultChallengeFormData(),
        ...challenge,
        startDate: challenge.startDate || '',
        endDate: challenge.endDate || '',
      }
    : getDefaultChallengeFormData();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ChallengeFormData>({
    defaultValues,
  });

  const [selectedTeams, setSelectedTeams] = useState<TeamOption[]>(
    challenge?.teams?.ids?.map((id: string) => teams.find((t) => t.id === id)).filter(Boolean) || []
  );

  const handleFormSubmit = (data: ChallengeFormData) => {
    const cleanData = removeTypename(data);
    const startDate = data.startDate
      ? typeof data.startDate === 'string'
        ? data.startDate
        : data.startDate.toISOString()
      : undefined;
    const endDate = data.endDate
      ? typeof data.endDate === 'string'
        ? data.endDate
        : data.endDate.toISOString()
      : undefined;

    const submissionData = {
      ...cleanData,
      startDate,
      endDate,
      rules: {
        actionsCompleted: {
          isExclude: true,
          ids: [],
        },
      },
    };
    onSubmit(submissionData);
  };

  const steps = [
    {
      label: 'Header and Banner Settings',
      id: 'basic',
      component: <BasicInfoStep control={control} errors={errors} />,
    },
    {
      label: 'Description',
      id: 'description',
      component: <DescriptionStep control={control} errors={errors} />,
    },
    {
      label: 'Image',
      id: 'backgroundImage',
      component: <BackgroundImageStep control={control} errors={errors} />,
    },
    {
      label: 'Competition Type',
      id: 'competition-type',
      component: <CompetitionTypeStep control={control} />,
    },
    {
      label: 'Participating Teams',
      id: 'teams',
      component: (
        <TeamsStep
          control={control}
          teams={teams}
          selectedTeams={selectedTeams}
          setSelectedTeams={setSelectedTeams}
          setValue={setValue}
          watch={watch}
          challengeStatus={challenge?.status}
          startDate={challenge?.startDate}
          endDate={challenge?.endDate}
        />
      ),
    },
    {
      label: 'General Settings',
      id: 'settings',
      component: <SettingsStep control={control} watch={watch} errors={errors} />,
    },
    {
      label: 'Review',
      id: 'review',
      component: <ReviewStep control={control} />,
    },
  ];

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} style={{ maxWidth: 1200 }}>
      <PageHeader
        title={getChallengeFormHeaderTitle(challenge)}
        onArrowClick={onCancel}
        buttonText={isEditing ? 'Save Changes' : 'Create Challenge'}
        buttonType="submit"
        buttonDisabled={isLoading}
      />

      {isLoading && <LinearProgress />}

      <Stepper orientation="vertical">
        {steps.map((item, index: number) => (
          <StepperComponent key={item.id} labelText={item.label} componentIndex={index}>
            {item.component}
          </StepperComponent>
        ))}

        <Step active completed={false}>
          <StepLabel icon={steps.length + 1}>
            <span
              style={{
                fontSize: 20,
                textTransform: 'capitalize',
                fontWeight: 'bold',
              }}
            >
              All set!
            </span>
          </StepLabel>
        </Step>

        <PageHeader
          onArrowClick={onCancel}
          buttonText={isEditing ? 'Save Changes' : 'Create Challenge'}
          buttonType="submit"
          buttonDisabled={isLoading}
          id="step-submit"
        />
      </Stepper>
    </form>
  );
};

export default ChallengeForm;
